/**
 * Home template
 * This file contains logic specific to a template.
 * It is intended to ONLY be loaded on templates that require
 * this specific functionality.
 **/

import Global from './lib/global';
import Logger from './lib/logger';
import CheckoutButton from './blocks/CheckoutButton';
import DiscountCodeDetector from './lib/DiscountCodeDetector';
import DiscountBar from './blocks/DiscountBar';
import Counter from './blocks/Counter';
import SidebarModal from './sections/SidebarModal';
import RotatingHeadline from './blocks/RotatingHeadline';
import { initTooltipCards, keepAllPanelsInViewport } from './blocks/TooltipCard';
import { initAccessibleGroups } from './blocks/AccessibleGroup';
import B2bOrderForm from './blocks/B2bOrderForm';
import B2bWelcomeDialog from './blocks/B2bWelcomeDialog';
import PdpPurchase from './blocks/PdpPurchase';

// Vars
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

const NAME = 'Base';

let discountCodeDetector: DiscountCodeDetector | null = null;
let checkoutButtonInstances: CheckoutButton[] = [];
let checkoutButtonObserver: MutationObserver | null = null;

// Logic
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

/**
 * init - Initialize the page
 **/
const init = () => {
	Logger.info(`${NAME} > init`);
	initDiscountCodeDetector();

	initCheckoutButtons();
	setupCheckoutButtonObserver();
	initDiscountBars();
	initCounters();
	initSidebarModal();
	initRotatingHeadlines();
	initTooltipCards();
	initAccessibleGroups();
	initB2bOrderForms();
	initB2bWelcomeDialogs();
	initPdpPurchases();
	initThemeEditorReloads();
}

/**
 * initPdpPurchases - Initialize the redesigned PDP's buy core
 */
const initPdpPurchases = (root: ParentNode = document) => {
	Logger.info(`${NAME} > initPdpPurchases`);
	root.querySelectorAll<HTMLElement>('[data-pdp-purchase]').forEach(pdpPurchase => {
		new PdpPurchase(pdpPurchase).init();
	});
}

/**
 * initThemeEditorReloads - Bind blocks inside a section the theme editor re-rendered in place
 *
 * Editing any block setting swaps the whole section's markup, and this bundle only runs once.
 */
const initThemeEditorReloads = () => {
	document.addEventListener('shopify:section:load', (event) => {
		if (!(event.target instanceof Element)) return;

		initPdpPurchases(event.target);
		// Tooltip listeners are delegated from document; only the new panels need placing.
		keepAllPanelsInViewport(event.target);
	});
}

/**
 * initB2bOrderForms - Initialize the wholesale product page's order form
 */
const initB2bOrderForms = () => {
	Logger.info(`${NAME} > initB2bOrderForms`);
	const orderForms = document.querySelectorAll<HTMLFormElement>('[data-b2b-order-form]');
	if (!orderForms) return;

	orderForms.forEach(orderForm => {
		new B2bOrderForm(orderForm).init();
	});
}

/**
 * initB2bWelcomeDialogs - Initialize the wholesale sign-in welcome dialog
 */
const initB2bWelcomeDialogs = () => {
	Logger.info(`${NAME} > initB2bWelcomeDialogs`);
	const welcomeDialogs = document.querySelectorAll<HTMLDialogElement>('[data-b2b-welcome]');
	if (!welcomeDialogs) return;

	welcomeDialogs.forEach(welcomeDialog => {
		new B2bWelcomeDialog(welcomeDialog).init();
	});
}

/**
 * initCheckoutButtons - Initialize the checkout buttons
 */
const initCheckoutButtons = () => {
	Logger.info(`${NAME} > initCheckoutButtons`);

	// Clear all existing bindings
	checkoutButtonInstances.forEach(instance => {
		instance.destroy();
	});
	checkoutButtonInstances = [];

	// Find all checkout buttons (including newly added ones)
	const checkoutButtons = document.querySelectorAll<HTMLElement>('[data-checkout-button]');
	if (!checkoutButtons.length || !discountCodeDetector) return;

	const detector = discountCodeDetector;

	// Initialize all checkout buttons and store instances
	checkoutButtons.forEach(checkoutButton => {
		const instance = new CheckoutButton(checkoutButton, detector);
		instance.init();
		checkoutButtonInstances.push(instance);
	});
}

/**
 * setupCheckoutButtonObserver - Set up MutationObserver to watch for dynamically added checkout buttons
 */
const setupCheckoutButtonObserver = () => {
	Logger.info(`${NAME} > setupCheckoutButtonObserver`);

	// Create observer to watch for added nodes
	checkoutButtonObserver = new MutationObserver((mutations) => {
		let shouldReinit = false;

		mutations.forEach((mutation) => {
			mutation.addedNodes.forEach((node) => {
				// Check if the added node is a checkout button or contains one
				if (node instanceof Element) {
					if (node.hasAttribute('data-checkout-button')) {
						shouldReinit = true;
					} else if (node.querySelectorAll('[data-checkout-button]').length > 0) {
						shouldReinit = true;
					}
				}
			});
		});

		if (shouldReinit) {
			Logger.info(`${NAME} > checkout buttons detected in DOM mutations, reinitializing...`);
			initCheckoutButtons();
		}
	});

	// Start observing the document body for child additions
	checkoutButtonObserver.observe(document.body, {
		childList: true,
		subtree: true
	});
}

/**
 * initDiscountCodeDetector --- Initialization block for the DiscountCodeDetector
 */
const initDiscountCodeDetector = () => {
	Logger.info(`${NAME} > initDiscountCodeDetector`);
	discountCodeDetector = new DiscountCodeDetector();
}

/**
 * initDiscountBars - Initialize all discount bars
 */
const initDiscountBars = () => {
	Logger.info(`${NAME} > initDiscountBars`);
	const discountBars = document.querySelectorAll<HTMLElement>('[data-discount-bar]');
	if (!discountCodeDetector) return;

	const detector = discountCodeDetector;
	discountBars.forEach(discountBar => {
		const discountBarInstance = new DiscountBar(discountBar, detector);
		discountBarInstance.init();
	});
}

/**
 * initCounters - Initialize the counters
 */
const initCounters = () => {
	Logger.info(`${NAME} > initCounters`);
	const counters = document.querySelectorAll<HTMLElement>('[data-counter]');

	counters.forEach(counter => {
		new Counter(counter).init();
	});
}

/**
 * initRotatingHeadlines - Initialize the rotating headlines
 */
const initRotatingHeadlines = () => {
	Logger.info(`${NAME} > initRotatingHeadlines`);
	const rotatingHeadlines = document.querySelectorAll<HTMLElement>('[data-rotating-headline]');

	rotatingHeadlines.forEach(rotatingHeadline => {
		new RotatingHeadline(rotatingHeadline).init();
	});
}

/**
 * initSidebarModal - Initialize the sidebar modal
 */
const initSidebarModal = () => {
	Logger.info(`${NAME} > initSidebarModal`);
	const sidebarModals = document.querySelectorAll<HTMLElement>('[data-sidebar-modal]');

	sidebarModals.forEach(sidebarModal => {
		new SidebarModal(sidebarModal).init();
	});

	const sidebarModalOpenButtons = document.querySelectorAll<HTMLElement>('[data-sidebar-modal-open]');

	sidebarModalOpenButtons.forEach(sidebarModalOpenButton => {
		sidebarModalOpenButton.addEventListener('click', (e) => {
			e.preventDefault();
			const sidebarModalId = sidebarModalOpenButton.dataset.sidebarModalOpen;
			document.dispatchEvent(new CustomEvent('sidebar-modal:open', { detail: { sidebarModalId } }));
		});
	});
}

// Bootstrap global functionality
Global();

// Bootstrap page-specific functionality
init();

// Do not edit below this line...
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
Logger.log(`${NAME} > loaded`);
